import { WebappResponse } from '@eventbrite/event-listing-service-client';
import { Stage } from '@eventbrite/webapp-constants';
import { AppProps } from 'next/app';

export type BasicInfo = NonNullable<WebappResponse['basicInfo']> & {
  // Adding properties here as OpenAPI schema doesn't include them
  isListed: boolean; // Property used in runtime (see dataLayer.ts) but missing from generated types
  isFree: boolean; // Property used in runtime but missing from generated types
  locale: string; // Property used in runtime but missing from generated types
  currency: string; // Property used in runtime but missing from generated types
  ticketsBy: string; // Property used in runtime but missing from generated types
  isParentEvent: boolean; // Property used in runtime but missing from generated types
};
export type Gallery = WebappResponse['gallery'];
export type Organizer = NonNullable<BasicInfo['organizer']>;
export type Venue = NonNullable<BasicInfo['venue']>;
export type Tags = NonNullable<WebappResponse['tags']>;
export type Tag = NonNullable<Tags[number]>;
export type StructuredContent = NonNullable<WebappResponse['structuredContent']>;

export const EVENT_STATUS = {
  UNSAVED: 'unsaved',
  DRAFT: 'draft',
  CANCELLED: 'cancelled',
  LIVE: 'live',
  STARTED: 'started',
  ENDED: 'ended',
  COMPLETED: 'completed',
  DELETED: 'deleted',
  POSTPONED: 'postponed',
};

export const VERTICAL = {
  TICKET: 'ticket',
  REGISTRATION: 'registration',
};

export type BaseProps = {
  stage: Stage;
};

export interface ListingsAppProps extends AppProps, BaseProps {
  serverUrl: string;
  rumGuestRoleArn: string;
  identityPoolId: string;
  applicationIds: Record<string, string>;
  requestLocale: string;
}

export enum RefundPolicyStatus {
  NOT_ACCEPTED = 'notAccepted',
  ACCEPTED = 'accepted',
  NOT_DEFINED = 'notDefined',
}

export enum RefundPolicyCode {
  FLEXIBLE = 'flexible',
  MODERATE = 'moderate',
  STRICT = 'strict',
  CUSTOM = 'custom',
}

export type RefundPolicyProps = {
  isPaidEvent?: boolean;
  refundPolicyCode?: string;
  refundPolicyDescription?: string;
  refundPolicyIsNoRefunds?: boolean;
  isRefundRequestAllowed?: boolean;
  validDays?: number;
  refundPolicyOrganizer?: boolean;
};

export interface Duration {
  totalSeconds: number;
  days: number;
  hours: number;
  minutes: number;
}

export interface HighlightsProps {
  accepts_refunds?: boolean;
  age_restriction?: string;
  duration?: Duration;
  eticket?: boolean;
  freeParking?: boolean;
  paidParking?: boolean;
  notAvailableParking?: boolean;
}

export interface Location {
  latitude: number;
  longitude: number;
}

export interface EventMapScopedProps {
  venueName: string;
  venueAddress: string;
  location: Location;
  gMapsApiKey: string;
  shouldDisplayMap: boolean;
}

export type InPersonLocation = {
  hasAddress: true;
  isOnlineEvent?: false;
  venueMultilineAddress: string[];
  venueName: string;
  localityPlaceId: string;
};

export type InPersonMap = Omit<EventMapScopedProps, 'venueName' | 'venueAddress'>;

export type OnlineLocation = {
  hasAddress?: false;
  isOnlineEvent: true;
  onlineEventUrl?: string;
};

export type TBDLocation = {
  hasAddress: false;
  isOnlineEvent: false;
};

export type LocationProps = InPersonLocation | OnlineLocation | TBDLocation;

declare global {
  interface Window {
    EBWidgets?: {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      createWidget: (params: any) => void;
    };
  }
}
